import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { fadeInUp } from '../lib/animations';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { BACKEND_URL } from '../lib/backend';

const API = `${BACKEND_URL}/api`;

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    const verify = async () => {
      try {
        const res = await fetch(`${API}/auth/verify-email?token=${token}`);
        const data = await res.json();
        if (res.ok && data.verified) {
          setStatus('success');
          setMessage('Your email has been verified successfully!');
          refreshUser();
        } else {
          setStatus('error');
          setMessage(data.detail || 'Verification failed.');
        }
      } catch {
        setStatus('error');
        setMessage('Something went wrong. Please try again.');
      }
    };
    verify();
  }, [token, refreshUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-8">
      <motion.div
        {...fadeInUp}
        className="max-w-md w-full text-center"
        data-testid="verify-email-page"
      >
        {status === 'loading' && (
          <div className="space-y-4">
            <Loader2 className="w-16 h-16 text-lime mx-auto animate-spin" />
            <h1 className="font-heading text-2xl font-bold">Verifying your email...</h1>
            <p className="text-[#A1A1AA]">Please wait a moment.</p>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-4">
            <CheckCircle className="w-16 h-16 text-lime mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Email Verified!</h1>
            <p className="text-[#A1A1AA]">{message}</p>
            <p className="text-[#A1A1AA]">You now have full access to order dubplates and message artists.</p>
            <Link to="/artists">
              <Button className="btn-primary mt-4" data-testid="browse-artists-btn">
                Browse Artists
              </Button>
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-4">
            <XCircle className="w-16 h-16 text-red-500 mx-auto" />
            <h1 className="font-heading text-2xl font-bold">Verification Failed</h1>
            <p className="text-[#A1A1AA]">{message}</p>
            <div className="flex gap-3 justify-center mt-4">
              <Link to="/settings">
                <Button variant="outline" data-testid="go-settings-btn">
                  Go to Settings
                </Button>
              </Link>
              <Link to="/">
                <Button className="btn-primary" data-testid="go-home-btn">
                  Go Home
                </Button>
              </Link>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
